import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, LogOut, LayoutDashboard, Gem, Phone, MessageSquareQuote, KeyRound, Link2, BookOpen } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { adminApi, getToken, setToken } from "../components/admin/api";
import { AdminLogin } from "../components/admin/AdminLogin";
import { Overview } from "../components/admin/Overview";
import { ContactEditor } from "../components/admin/ContactEditor";
import { PackagesEditor } from "../components/admin/PackagesEditor";
import { ReviewsEditor } from "../components/admin/ReviewsEditor";
import { LinksEditor } from "../components/admin/LinksEditor";
import { GuidesEditor } from "../components/admin/GuidesEditor";
import { PasswordForm } from "../components/admin/PasswordForm";
import { useSite } from "../context/SiteContext";

const NAV = [
  { id: "overview", label: "Genel Bakış", icon: LayoutDashboard, desc: "Sitenin kısa özeti ve hızlı erişim." },
  { id: "packages", label: "Paketler", icon: Gem, desc: "Paket ekle, fiyatları ve özellikleri düzenle, sırala." },
  { id: "contact", label: "İletişim", icon: Phone, desc: "WhatsApp destek hattı, Telegram ve Instagram bilgileri." },
  { id: "links", label: "Bağlantılar", icon: Link2, desc: "Web giriş paneli ve uygulama indirme bağlantıları." },
  { id: "guides", label: "Kurulum Rehberi", icon: BookOpen, desc: "Her cihazın kurulum adımları ve videosu." },
  { id: "reviews", label: "Yorumlar", icon: MessageSquareQuote, desc: "Sitede görünen müşteri yorumlarını yönet." },
  { id: "password", label: "Şifre", icon: KeyRound, desc: "Admin giriş şifreni değiştir." },
];

const NavItem = ({ n, active, onClick }) => (
  <button onClick={onClick} data-testid={`admin-tab-${n.id}`}
    className={`relative flex shrink-0 items-center gap-3 rounded-xl px-4 h-11 text-sm font-medium transition-colors ${active ? "text-white" : "text-[#c9c6f0] hover:text-white hover:bg-white/[0.04]"}`}>
    {active && <motion.span layoutId="admin-nav" className="absolute inset-0 rounded-xl bg-[#5b5fd6]/20 ring-1 ring-[#8a8dff]/40" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
    <n.icon size={17} className="relative" />
    <span className="relative">{n.label}</span>
  </button>
);

export default function AdminPage() {
  const { reload } = useSite();
  const [user, setUser] = useState(getToken() ? null : false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    adminApi.get("/site").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    adminApi.get("/auth/me").then((r) => setUser(r.data)).catch(() => { setToken(null); setUser(false); });
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const saved = () => { load(); reload(); };
  const logout = () => { setToken(null); setUser(false); };

  if (user === null) return <div className="min-h-screen bg-[#07061f]" />;
  if (!user) return <><AdminLogin onLogin={setUser} /><Toaster /></>;

  const cur = NAV.find((n) => n.id === tab);

  return (
    <div className="min-h-screen bg-[#07061f] text-white lg:flex" data-testid="admin-panel">
      <div className="pointer-events-none fixed -top-40 right-0 h-[28rem] w-[28rem] rounded-full bg-[#5b5fd6]/20 blur-[120px]" />
      <aside className="lg:sticky lg:top-0 lg:h-screen lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-white/[0.06] bg-[#0b0a2e]/80 backdrop-blur-xl flex flex-col">
        <div className="flex items-center justify-between px-5 lg:px-6 h-16 lg:h-20">
          <img src="/img/logo-t.png" alt="Dolunay TV" className="h-9 w-auto" />
          <button onClick={logout} className="lg:hidden p-2 text-[#c9c6f0]" aria-label="Çıkış" data-testid="admin-logout-mobile"><LogOut size={18} /></button>
        </div>
        <p className="hidden lg:block px-6 pb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#a4a6ff]">Yönetim</p>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto px-3 lg:px-4 pb-3 lg:pb-0">
          {NAV.map((n) => <NavItem key={n.id} n={n} active={tab === n.id} onClick={() => setTab(n.id)} />)}
        </nav>
        <div className="hidden lg:block mt-auto p-4">
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-[#8a8dff] to-[#5b5fd6] font-display font-bold">{user.email?.[0]?.toUpperCase()}</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{user.email}</p>
                <p className="text-xs text-[#a4a6ff]">Yönetici</p>
              </div>
            </div>
            <button onClick={logout} data-testid="admin-logout" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 h-10 text-sm text-[#c9c6f0] hover:text-white hover:bg-white/[0.05] transition-colors">
              <LogOut size={15} /> Çıkış yap
            </button>
          </div>
        </div>
      </aside>

      <main className="relative flex-1 min-w-0">
        <header className="sticky top-0 z-20 border-b border-white/[0.06] bg-[#07061f]/70 backdrop-blur-xl">
          <div className="mx-auto max-w-5xl px-5 md:px-8 h-20 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-xl md:text-2xl font-bold" data-testid="admin-section-title">{cur.label}</h1>
              <p className="hidden sm:block text-sm text-[#c9c6f0] truncate">{cur.desc}</p>
            </div>
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site"
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 h-10 text-sm font-medium hover:bg-white/[0.08] transition-colors">
              <ExternalLink size={15} /> Siteyi gör
            </a>
          </div>
        </header>
        <div className="mx-auto max-w-5xl px-5 md:px-8 py-8 md:py-10">
          {!data ? <p className="text-[#c9c6f0]">Yükleniyor...</p> : (
            <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
              {tab === "overview" && <Overview data={data} go={setTab} />}
              {tab === "packages" && <PackagesEditor initial={data.packages} onSaved={saved} />}
              {tab === "contact" && <ContactEditor initial={data.contact} onSaved={saved} />}
              {tab === "links" && <LinksEditor initial={data.links} onSaved={saved} />}
              {tab === "guides" && <GuidesEditor initial={data.guides} legacyVideos={data.links?.videos} onSaved={saved} />}
              {tab === "reviews" && <ReviewsEditor initial={data.reviews} onSaved={saved} />}
              {tab === "password" && <PasswordForm />}
            </motion.div>
          )}
        </div>
      </main>
      <Toaster theme="dark" />
    </div>
  );
}
